/**
 * useOptionChainStream — Silent-resilience WebSocket hook for live option chain updates
 *
 * Part A: Silent recovery mechanism
 * - On page load: fetch one snapshot via REST
 * - Join WS room and stream ticks
 * - If WS drops or stalls: auto-switch to REST refresh (every 7s) until WS resumes
 * - UI looks normal at all times — no "mode" indicators
 *
 * Returns: { update, freshnessStatus, integrity, isStreaming, isPollingFallback }
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useRealtimeContext } from "../providers/RealtimeProvider";
import { isMarketOpen } from "../utils/marketHours";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recovery polling interval when WS is down
const RECOVERY_INTERVAL_MS = 7000;
// How long without a WS tick before switching to recovery (during market hours)
const STALE_THRESHOLD_MS = 5000;

export function useOptionChainStream({ underlying, expiry }) {
  const ctx = useRealtimeContext();
  const [update, setUpdate] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPollingFallback, setIsPollingFallback] = useState(false);
  const lastWsTickRef = useRef(null);
  const recoveryTimerRef = useRef(null);
  const staleTimerRef = useRef(null);
  // Track recovery state for health reporting
  const recoveryStartRef = useRef(null);
  const disconnectCountRef = useRef(0);

  const room = useMemo(
    () => (underlying && expiry ? `oc:${underlying}:${expiry}` : null),
    [underlying, expiry]
  );

  const freshnessStatus = useMemo(() => {
    if (!update) return "EOD";
    return update.freshness_status || "EOD";
  }, [update]);

  const integrity = useMemo(() => {
    if (!update) return null;
    return update.integrity || null;
  }, [update]);

  // ── REST snapshot fetch (one-time or recovery) ──
  const fetchSnapshot = useCallback(async () => {
    if (!underlying) return;
    try {
      const url = `${API_URL}/api/market/optionchain/${underlying}`;
      const resp = await fetch(url);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data && data.strikes && data.strikes.length > 0) {
        const rows = (data.strikes || []).map((s) => ({
          strike: s.strike,
          ce: s.ce || null,
          pe: s.pe || null,
        }));
        setUpdate({
          type: "option_chain_update",
          underlying,
          expiry: expiry || "",
          as_of: new Date().toISOString(),
          source_mode: data.data_source || "REST_SNAPSHOT",
          freshness_status: data.integrity?.source_mode || "EOD",
          integrity: data.integrity || null,
          spot: data.spot_price,
          rows,
        });
      }
    } catch {
      // Silent — will retry on next recovery interval
    }
  }, [underlying, expiry]);

  // ── Recovery start/stop ──
  const startRecovery = useCallback(() => {
    if (recoveryTimerRef.current) return; // already running
    setIsPollingFallback(true);
    if (!recoveryStartRef.current) {
      recoveryStartRef.current = Date.now();
    }
    disconnectCountRef.current += 1;
    fetchSnapshot(); // Immediate first fetch
    recoveryTimerRef.current = setInterval(fetchSnapshot, RECOVERY_INTERVAL_MS);

    // Log for internal monitoring (Part B: server-side picks this up)
    console.warn(`[OC-WS] Recovery mode activated for ${underlying}:${expiry}`);
  }, [fetchSnapshot, underlying, expiry]);

  const stopRecovery = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearInterval(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    if (isPollingFallback) {
      console.info(`[OC-WS] Recovery mode deactivated — WS resumed for ${underlying}:${expiry}`);
    }
    setIsPollingFallback(false);
    recoveryStartRef.current = null;
  }, [isPollingFallback, underlying, expiry]);

  useEffect(() => {
    if (!room || !ctx) return;

    const socket = ctx.getSocket();
    if (!socket) {
      // No socket available at all — use recovery silently
      if (isMarketOpen()) {
        startRecovery();
      } else {
        // Market closed — just fetch one snapshot
        fetchSnapshot();
      }
      return () => stopRecovery();
    }

    // Join room
    socket.emit("join", { room });
    setIsStreaming(true);

    const handler = (payload) => {
      if (
        payload?.underlying === underlying &&
        payload?.expiry === expiry
      ) {
        setUpdate(payload);
        lastWsTickRef.current = Date.now();
        setIsStreaming(true);
        // WS is live — stop any recovery polling immediately
        stopRecovery();

        // Reset stale timer — if no tick for STALE_THRESHOLD during market hours, enter recovery
        if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
        staleTimerRef.current = setTimeout(() => {
          if (isMarketOpen()) {
            setIsStreaming(false);
            startRecovery();
          }
        }, STALE_THRESHOLD_MS);
      }
    };

    socket.on("option_chain_update", handler);

    // Handle disconnect → recovery
    const onDisconnect = () => {
      setIsStreaming(false);
      if (isMarketOpen()) {
        startRecovery();
      }
    };

    const onReconnect = () => {
      socket.emit("join", { room });
      setIsStreaming(true);
      stopRecovery();
    };

    socket.on("disconnect", onDisconnect);
    socket.on("connect", onReconnect);

    return () => {
      socket.off("option_chain_update", handler);
      socket.off("disconnect", onDisconnect);
      socket.off("connect", onReconnect);
      socket.emit("leave", { room });
      setIsStreaming(false);
      stopRecovery();
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    };
  }, [room, underlying, expiry, ctx, startRecovery, stopRecovery, fetchSnapshot]);

  return {
    update,
    freshnessStatus,
    integrity,
    isStreaming,
    isPollingFallback,
  };
}

export default useOptionChainStream;
